import { PDFDocument } from 'pdf-lib';
import { FILE_EXTENSIONS_TO_VALIDATE } from '@/_constants/files';
import { uploadDocument } from '@/_api/client-questionnaires';
import { FileWrapper } from '@/_contexts/DocumentsProvider/components/DocumentsUploadDialog/components/Files/Files.types';

export const pdfCannotBeOpened = async (
  pdfData: string | Uint8Array | ArrayBuffer,
): Promise<boolean> => {
  try {
    // Try to load the PDF document
    await PDFDocument.load(pdfData, { ignoreEncryption: false });
    // If we get here, the PDF is not password protected and can be opened
    return false;
  } catch (error) {
    return true;
  }
};

/**
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/Window/btoa#converting_arbitrary_binary_data}
 */
async function bytesToBase64DataUrl<T>(
  // eslint-disable-next-line no-undef -- TODO: Remove disable as part of SOR-14040
  bytes: BlobPart,
  type = 'image/jpeg',
): Promise<T> {
  return await new Promise((resolve, reject) => {
    const reader = Object.assign(new FileReader(), {
      onload: () => resolve(reader.result as T),
      onerror: () => reject(reader.error),
    });
    // calling `readAsDataURL` on the reader ensures this function resolves to a string containing the base64 data URL
    reader.readAsDataURL(new File([bytes], '', { type }));
  });
}

/**
 * Convert an HEIC image (from a remote URL) to a JPEG image data URL.
 */
export const convertHeicToJPEG = async (url: string): Promise<string> => {
  const response = await fetch(url);
  const buffer = Buffer.from(await response.arrayBuffer());

  // Dynamically import heic-convert only when needed to reduce bundle size
  const heicConvert = (await import('heic-convert/browser')).default;
  const image = await heicConvert({
    buffer: buffer as unknown as ArrayBufferLike,
    format: 'JPEG',
  });
  return await bytesToBase64DataUrl<string>(new Uint8Array(image));
};

export const getExtensionOfFilename = (filename?: string): string => {
  if (!filename) return '';

  const match = filename.match(/\.([a-zA-Z0-9]+)(?=\?|$)/);

  return match ? match[1].toLowerCase() : '';
};

/**
 * Get the true file extension from analyzing file header.
 */
export const getTrueExtension = async (file: File): Promise<string> =>
  new Promise((resolve) => {
    const reader: FileReader = new FileReader();

    reader.onloadend = () => {
      const arr = new Uint8Array(reader.result as unknown as any).subarray(
        0,
        4,
      );
      let header = '';

      for (let i = 0; i < arr.length; i++) {
        header += arr[i].toString(16);
      }

      switch (header) {
        case '89504e47':
          return resolve('png');
        case '47494638':
          return resolve('gif');
        case '5b6f626a': // for E2E testing
        case '25504446':
          return resolve('pdf');
        case 'ffd8ffe0':
        case 'ffd8ffe1':
        case 'ffd8ffe2':
          return resolve('jpeg');
        default:
          return resolve('unknown');
      }
    };

    reader.readAsArrayBuffer(file);
  });

export const validateFile = async (
  file: File,
): Promise<{
  isValid: boolean;
  extension: string;
  isProtectedPDF: boolean;
}> => {
  const currentExtension = getExtensionOfFilename(file.name);

  const fileDetails: {
    isValid: boolean;
    extension: string;
    isProtectedPDF: boolean;
  } = {
    isValid: true,
    extension: currentExtension,
    isProtectedPDF: false,
  };

  // Skip check if not in list of valid extensions
  if (!FILE_EXTENSIONS_TO_VALIDATE.includes(currentExtension)) {
    return fileDetails;
  }

  // check if it's valid
  const trueExtension: string = await getTrueExtension(file);
  // TODO: Fix this. Not working properly
  // const isValid: boolean =
  //   trueExtension === currentExtension || currentExtension === 'heic';

  // if (!isValid) fileDetails.isValid = false;

  // if it's a valid pdf, check if it's protected
  if (trueExtension === 'pdf') {
    const pdfData = await file.arrayBuffer();
    const isProtectedPDF: boolean = await pdfCannotBeOpened(pdfData);

    fileDetails.isProtectedPDF = isProtectedPDF;
  }

  return fileDetails;
};

export const saveAs = (uri: string, filename?: string): Promise<void> => {
  return new Promise((resolve) => {
    const link = document.createElement('a');

    if (typeof link.download === 'string') {
      link.href = uri;
      link.download = filename || '';
      // Firefox requires the link to be in the body
      document.body.appendChild(link);
      // simulate click
      link.click();
      // remove the link when done
      setTimeout(() => {
        document.body.removeChild(link);
        resolve();
      }, 500);
    } else {
      globalThis.open(uri, '_blank');
      resolve();
    }
  });
};

export const getUniqueFiles = (
  oldFiles: Array<File> = [],
  newFiles: Array<File> = [],
): Array<File> => {
  if (!newFiles?.length) return oldFiles;

  const allFiles: Array<File> = [...oldFiles, ...newFiles];
  const uniqueFiles: Array<File> = allFiles.filter(
    (file, index, self) =>
      index ===
      self.findIndex((f) => f.name === file.name && f.size === file.size),
  );

  return uniqueFiles;
};

export const removeIndexFile = (
  oldFiles: Array<FileWrapper> = [],
  index: number,
): Array<FileWrapper> => {
  const newFiles: Array<FileWrapper> = oldFiles.filter((_, i) => i !== index);

  return newFiles;
};

export const handleAcceptedUploadedFiles = async ({
  acceptedFiles,
  addToast,
  companyId,
  description,
  documentType,
  questionnaireId,
  requiresSplitting,
  selectedOptions,
  successfullyUploadedFileCallback,
  tags,
  token,
  uploadPath,
}: {
  acceptedFiles: Array<File>;
  addToast: any;
  companyId: number;
  description?: string;
  documentType?: string;
  questionnaireId: number;
  requiresSplitting?: boolean;
  selectedOptions?: Array<string>;
  successfullyUploadedFileCallback?: (file: File) => void;
  tags?: Array<string>;
  token: string;
  uploadPath: string;
}): Promise<any[]> => {
  try {
    const uploadPromises = acceptedFiles.map(async (file) => {
      const { isValid } = await validateFile(file);

      if (!isValid) {
        addToast(
          'error',
          'We were unable to process some of uploaded files as they appear to be INVALID. Kindly check those files and try uploading them again.',
        );
        return null;
      }

      // TODO: Fix this. Not working properly
      // if (isProtectedPDF) {
      //   addToast(
      //     'error',
      //     'The PDFs you uploaded are either password-protected or cannot be opened. Please either remove the password or upload alternative files.',
      //   );
      //   continue;
      // }

      const uploaded: any = await uploadDocument({
        companyId,
        description: description || '',
        documentName: file?.name,
        documentType,
        file,
        path: `/${uploadPath}`,
        questionnaireId,
        requiresSplitting,
        selectedOptions: selectedOptions || [],
        tags: tags || [],
        token,
      });

      if (uploaded?.is_duplicate) {
        addToast('error', `${file?.name} was already uploaded`, {
          error: uploaded,
        });
      } else if (successfullyUploadedFileCallback) {
        successfullyUploadedFileCallback(file);
      }

      return uploaded;
    });

    return (await Promise.all(uploadPromises)).filter(
      (result) => result !== null,
    );
  } catch (error) {
    addToast('error', 'File upload failed, please try again.', { error });
    return [];
  }
};

export function getDocumentName(source: string): string {
  return source.split('/')[source.split('/').length - 1];
}

export const downloadBlob = (blob: Blob, fileName: string) => {
  if (!blob || !fileName) return;

  try {
    const downloadUrl: string = URL.createObjectURL(blob);
    const link: HTMLAnchorElement = document.createElement('a');

    link.href = downloadUrl;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();

    link?.parentNode?.removeChild?.(link);
    URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error(error);
  }
};
